/* ============================================================
   ATHLO Rewards — storefront client for our own loyalty backend
   (Supabase functions/v1/loyalty). Replaces the Smile bridge:
     • captures ?ref=<id> referrals into a cookie, and attributes
       them to the signed-in member (server-verified)
     • fetches the member's REAL Reward-Points balance + tier
       (?state, per-customer token auth) and fills the rewards
       menu + locker panel with live numbers
   Reads member id + locker token straight off <body>. Inert-safe:
   no-ops when logged out or the endpoint isn't set.
   ============================================================ */
(function () {
  'use strict';
  var B = document.body;
  var EP = (B.getAttribute('data-loyalty-endpoint') || '').trim();      // direct Supabase URL (token auth)
  var PROXY = (B.getAttribute('data-loyalty-proxy') || '').trim();      // App Proxy path e.g. /apps/loyalty (preferred)
  var MEMBER = (B.getAttribute('data-member-id') || '').trim();
  // 2026-08-29: a FUNCTION, not a captured constant. This was `var TOKEN = <attribute>` read at
  // module load — i.e. before hub-session.js could possibly have finished minting — so with
  // `locker_token_in_dom` off it captured '' permanently, for the life of the page.
  //
  // That is currently harmless ONLY because `loyalty_proxy` is set, which routes identity through
  // Shopify's signed App Proxy and never sends this token at all. Clear that one setting and the
  // guard below (`!USE_PROXY && !TOKEN`) would early-return on every page load and loyalty would
  // silently stop working, with no error anywhere. An armed trap for whoever touched that setting
  // next. Reading at call time removes it whether or not the proxy is in play.
  function lockerToken() {
    try {
      var s = window.HubSession;
      return ((s && typeof s.token === 'function' && s.token()) ||
              (B.getAttribute('data-locker-token') || '')).trim();
    } catch (e) { return ''; }
  }
  // Prefer the App Proxy when configured: same-origin, and identity comes from Shopify's SIGNED request
  // (logged_in_customer_id) — the locker token is never sent. Falls back to the direct endpoint + token
  // headers when no proxy is set (default). Flip the `loyalty_proxy` theme setting the moment you set
  // SHOPIFY_APP_PROXY_SECRET on the function (setting the secret alone would 401 the direct path).
  var USE_PROXY = !!PROXY;
  var BASE = USE_PROXY ? PROXY : EP;

  // ---- referral capture: a visitor arriving via ?ref=<memberId> gets tagged (90d cookie) ----
  // The tag is a NON-ESSENTIAL cross-visit tracker, so it is deferred until the visitor accepts
  // analytics consent (PDPA: no non-essential data before acceptance). If consent was already
  // granted (this or a past visit) it writes now; otherwise it waits for the consent banner's
  // 'athlo:consent' event, and on decline it simply never writes.
  try {
    var q = new URLSearchParams(location.search).get('ref');
    if (q && /^[0-9]+$/.test(q)) {
      var setRefCookie = function () {
        try { document.cookie = 'athlo_ref=' + q + ';path=/;max-age=' + (90 * 86400) + ';samesite=lax'; } catch (e) {}
      };
      var refConsent = false;
      try { var rc = JSON.parse(localStorage.getItem('athlo:consent') || 'null'); refConsent = !!(rc && rc.analytics); } catch (e) {}
      if (refConsent) setRefCookie();
      else document.addEventListener('athlo:consent', function (ev) { if (ev && ev.detail && ev.detail.analytics) setRefCookie(); }, { once: true });
    }
  } catch (e) {}

  // Need a backend + a logged-in member. Direct mode also needs a locker token — but that is
  // deliberately NOT checked here any more: at module-load time the mint has not landed, so a
  // load-time check would refuse to start a module that would have worked a second later. The
  // per-request check in authHeaders() is where it belongs.
  if (!BASE || !MEMBER) return;

  function cookie(n) { var m = document.cookie.match('(?:^|; )' + n + '=([^;]*)'); return m ? decodeURIComponent(m[1]) : ''; }
  function fmt(n) { try { return (n || 0).toLocaleString(); } catch (e) { return String(n || 0); } }
  // Direct mode: identity in HEADERS (never the URL) so the bearer token stays out of logs.
  // Proxy mode: send NO identity headers — Shopify's App Proxy signs the request and the function
  // derives identity from the signed logged_in_customer_id, so the token is never transmitted at all.
  function authHeaders(extra) {
    var h = {};
    if (!USE_PROXY) { h['x-athlo-member'] = MEMBER; h['x-athlo-token'] = lockerToken(); }
    if (extra) for (var k in extra) h[k] = extra[k];
    return h;
  }

  // ---- referral attribution: if I arrived via someone's link, tell the backend (once) ----
  var ref = cookie('athlo_ref');
  if (ref && ref !== MEMBER && /^[0-9]+$/.test(ref)) {
    fetch(BASE + '?action=refer-attribute', {
      method: 'POST', headers: authHeaders({ 'content-type': 'application/json' }), body: JSON.stringify({ referrer: ref })
    }).then(function () { document.cookie = 'athlo_ref=;path=/;max-age=0'; }).catch(function () {});
  }

  // ---- fetch live state and paint the UI (re-runnable: called again after a redemption) ----
  function loadState() {
    // 2026-08-29: an auth failure here used to be completely silent — a 401 fell into `.catch(){}`
    // below, or came back `{ok:false}` and hit the bare `return`, and the member simply saw a
    // rewards panel that never filled in. Indistinguishable from "you have no points". Say it once,
    // where hub-errlog can page on it. Deliberately NOT surfaced in the UI: a blank panel is not
    // worth an error message to a member who cannot act on it, but it IS worth telling us.
    if (!USE_PROXY && !lockerToken()) {
      try { console.error('[hub-loyalty] no locker token in direct mode — points/tier will not load.'); } catch (e) {}
    }
    return fetch(BASE + '?state', { headers: authHeaders() })
      .then(function (r) {
        if (r.status === 401 || r.status === 403) {
          try { console.error('[hub-loyalty] state refused (' + r.status + ') — points/tier will not load.'); } catch (e) {}
        }
        return r.json();
      })
      .then(function (s) {
        if (!s || !s.ok) return;
        window.ATHLO_LOYALTY = s;               // { balance, lifetime, tier, multiplier, next, valuePerPoint, referrals, redeemable, redemptionEnabled }
        // Expose the verified referral-conversion count so the achievement engine (hub-profile.js)
        // can unlock "Spread the Word" on a REAL conversion, not just an invite-link copy.
        window.HUB_REFERRALS = (typeof s.referrals === 'number') ? s.referrals : 0;
        paint(s);
        renderRedeem(s);
        try { window.dispatchEvent(new CustomEvent('athlo:loyalty', { detail: s })); } catch (e) {}
      })
      .catch(function () {});
  }
  loadState();

  function paint(s) {
    var toNext = s.next ? (fmt(s.next.need) + ' pts to ' + s.next.name) : 'Top tier reached';
    // Rewards launcher panel (hub-rewards.liquid)
    set('hr-points-bal', fmt(s.balance) + ' pts');
    set('hr-points-tier', s.tier + ' · ' + toNext);
    set('hr-points-cta', 'Earning ' + s.multiplier + '×');
    renderPanelRedeem(s);                                // panel tiers from the SERVER catalogue, not a static setting
    // Locker "Rewards" panel (hub-locker.liquid)
    set('lk-rewards-h', fmt(s.balance) + ' points · ' + s.tier);
    set('lk-rewards-sub', s.next
      ? ("You're " + fmt(s.next.need) + ' pts from ' + s.next.name + ', earning ' + s.multiplier + '× on every order.')
      : ("Top tier — earning " + s.multiplier + '× on every order.'));
    // Highlight the member's current tier card in the on-page tier list
    [].forEach.call(document.querySelectorAll('.lk-tier'), function (c) {
      var nm = (c.querySelector('.lk-tier__name') || {}).textContent || '';
      c.classList.toggle('is-current', nm.trim() === s.tier);
    });
    paintMembership(s);
    paintBirthdayAsk();
  }
  function set(id, txt) { var el = document.getElementById(id); if (el) el.textContent = txt; }

  // Keep the launcher panel's "Redeem your points" list in lockstep with the SERVER catalogue - the
  // same `s.redeemable` the locker's #lk-redeem uses. A hand-authored theme setting drifted from the
  // backend (advertised a $5 / 250 tier the function never offered); driving the member's view from
  // the server makes that impossible. Guests / pre-JS keep the static teaser rendered by the liquid.
  // Display-only on purpose: the "Open my locker" CTA is where a redemption is actually made.
  function renderPanelRedeem(s) {
    var ul = document.getElementById('hr-redeem'); if (!ul) return;
    if (!s || !s.redemptionEnabled || !Array.isArray(s.redeemable) || !s.redeemable.length) return; // leave the teaser
    ul.textContent = '';
    s.redeemable.forEach(function (r) {
      var li = el('li', 'hr-redeem__row');
      li.appendChild(el('span', 'hr-redeem__reward', r.label));
      li.appendChild(el('span', 'hr-redeem__cost', fmt(r.cost) + ' pts'));
      ul.appendChild(li);
    });
  }

  // ---- the same ask, on every page (Rewards panel, #hr-bday) ---------------------------------
  // The Membership card's nudge below reaches only members who open the locker. This one rides
  // the Rewards panel, which theme.liquid renders site-wide, and sits directly above the "Ways to
  // earn" list that promises 50 points on their birthday.
  //
  // It is painted from TWO places on purpose. paint() covers "loyalty state arrived last"; the
  // hub:profile listener covers "the profile arrived last", which is the common case on a cold
  // load and the one that would otherwise leave the ask hidden until the next page view. Both
  // call the same predicate, so a birthday saved in the locker clears the ask without a reload.
  function paintBirthdayAsk() {
    var el = document.getElementById('hr-bday'); if (!el) return;
    el.hidden = !needsBirthday();
  }
  window.addEventListener('hub:profile', paintBirthdayAsk);

  // Does this member still owe us a birthday? The SAME test hub-profile.js completion() uses, and
  // deliberately so: it accepts EITHER the local full dob OR the synced 'MM-DD' bday, because only
  // bday crosses the wire. Testing dob alone would nag a member forever on any second device.
  // Unknown counts as "already has one" - a profile that has not loaded must never produce a nag.
  function needsBirthday() {
    try {
      if (!window.HubProfile || !window.HubProfile.get) return false;
      var p = window.HubProfile.get();
      // A profile that has not loaded yet answers `null`, and that is NOT the same as a member
      // with no birthday. Coercing it to {} here would nag every member for the few hundred
      // milliseconds before their profile arrives, which is exactly when they are looking.
      if (!p) return false;
      return !(/^\d{4}-\d{2}-\d{2}$/.test(p.dob || '') || /^\d{2}-\d{2}$/.test(p.bday || ''));
    } catch (e) { return false; }
  }

  // ---- Membership Tier card (UNIFIED POINTS) — replaces the retired XP Season card ---------------
  // One currency: tier + lifetime points (drives tier) + redeemable balance + progress to next tier +
  // the tier's perks. Fed entirely by the verified loyalty ?state (or an award response). Thresholds
  // MUST mirror core.ts / schema-loyalty.sql (loyalty_tier_for): Rookie 0 / Pro 500 / Elite 2000.
  var TIER_MIN = { Rookie: 0, Pro: 500, Elite: 2000, Legend: 5000 };
  // Tiers change the points multiplier and nothing else (core.ts TIERS). Name a perk here only once it exists.
  var TIER_PERKS = {
    Rookie: '1× points on every order · birthday points',
    Pro:    'Everything in Rookie + 1.25× points on every order',
    Elite:  'Everything in Pro + 1.5× points on every order',
    Legend: 'Invite-only recognition'
  };
  function paintMembership(s) {
    var card = document.getElementById('lk-membership'); if (!card) return;
    card.hidden = false;
    // Flat-membership mode: one "Member" status — hide the tier ladder (multiplier pill, progress bar).
    var mult = card.querySelector('.lk-membership__mult');
    var prog = card.querySelector('.lk-membership__prog');
    if (mult) mult.style.display = s.flat ? 'none' : '';
    if (prog) prog.style.display = s.flat ? 'none' : '';
    set('lk-mem-tier', s.flat ? 'Member' : s.tier);
    set('lk-mem-balance', fmt(s.balance));
    set('lk-mem-lifetime', fmt(s.lifetime));
    set('lk-mem-mult', s.multiplier + '×');
    set('lk-mem-perks', s.flat ? 'Earn points on every order and redeem them for rewards.' : (TIER_PERKS[s.tier] || ''));
    // progress within the current tier band (next.need = points remaining, so nextMin = lifetime + need)
    var min = TIER_MIN[s.tier] || 0;
    var nextMin = s.next ? (s.lifetime + s.next.need) : min;
    var pct = s.next && (nextMin - min) > 0 ? Math.max(0, Math.min(1, (s.lifetime - min) / (nextMin - min))) : 1;
    var bar = document.getElementById('lk-mem-bar'); if (bar) bar.style.width = (pct * 100).toFixed(1) + '%';
    set('lk-mem-next', s.next ? (fmt(s.next.need) + ' pts to ' + s.next.name) : 'Top tier reached');
    // In-app nudge (the free half of "notifications"): if they can already afford a reward, tell them;
    // otherwise nudge progress to the next tier. Shows only when redemption is live.
    var nudge = document.getElementById('lk-mem-nudge');
    if (nudge) {
      var best = null;
      if (s.redemptionEnabled && s.redeemable) s.redeemable.forEach(function (r) { if (r.affordable && (!best || r.cost > best.cost)) best = r; });
      if (best) { nudge.textContent = '🎉 You have enough points for ' + best.label + ' — redeem it below.'; nudge.hidden = false; }
      else if (s.next && s.next.need <= 150) { nudge.textContent = "🔥 You're only " + fmt(s.next.need) + ' points from ' + s.next.name + '!'; nudge.hidden = false; }
      // THE BIRTHDAY ASK, and it sits HERE on purpose — in the branch that previously rendered
      // nothing at all, so it can never displace a redemption or a tier message.
      //
      // Why it needs a place this prominent: on 2026-08-05 the birthday bonus, the email and the
      // schedule were all finished and live, and ONE member in eleven had a birthday on file — and
      // that one turned out to be a test value, so the real count was zero. The field existed in the
      // profile editor and as one of nine equal chips on the onboarding card; nobody ever filled it.
      // A feature nobody can trigger is worth exactly nothing, however well it is built.
      //
      // The privacy line is not decoration, it is the objection: people decline birthdays because
      // they read it as handing over their age. Only MM-DD ever leaves the device (hub-profile.js
      // sync()), so saying so is both true and the whole argument.
      else if (needsBirthday()) {
        nudge.textContent = '🎂 Add your birthday — 50 points land on the day. Only the day and month are ever stored, never the year.';
        nudge.hidden = false;
        nudge.classList.add('is-action');
        nudge.setAttribute('role', 'button');
        nudge.setAttribute('tabindex', '0');
        if (!nudge.dataset.bdayWired) {
          nudge.dataset.bdayWired = '1';
          var jump = function () { if (typeof window.HubLockerJump === 'function') window.HubLockerJump('lk-dob'); };
          nudge.addEventListener('click', jump);
          nudge.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); jump(); } });
        }
      }
      else { nudge.hidden = true; nudge.classList.remove('is-action'); nudge.removeAttribute('role'); nudge.removeAttribute('tabindex'); }
    }
    paintActivity(s.activity);
  }
  // Recent-points feed: every point move (purchase, badge, challenge, manual grant) as a friendly line.
  // Labels are server-provided + customer-safe; built with textContent so nothing can inject markup.
  // If the backend doesn't send `activity` yet (older deploy), hide the whole block rather than show
  // a misleading empty state.
  function paintActivity(rows) {
    var wrap = document.querySelector('.lk-membership__activity');
    var box = document.getElementById('lk-mem-activity'); if (!box) return;
    if (!Array.isArray(rows)) { if (wrap) wrap.style.display = 'none'; return; }
    if (wrap) wrap.style.display = '';
    box.textContent = '';
    if (!rows.length) {
      var e = document.createElement('p'); e.className = 'lk-mem-act-empty';
      e.textContent = 'Earn points and they’ll show up here.'; box.appendChild(e); return;
    }
    rows.slice(0, 6).forEach(function (row) {
      var d = document.createElement('div'); d.className = 'lk-mem-act';
      var l = document.createElement('span'); l.textContent = row.label || 'Points';
      var b = document.createElement('b'); b.className = (row.delta > 0 ? 'pos' : 'neg');
      b.textContent = (row.delta > 0 ? '+' : '') + fmt(row.delta);
      d.appendChild(l); d.appendChild(b); box.appendChild(d);
    });
  }

  // ---- Award an unlocked achievement to the redeemable ledger (UNIFIED POINTS) -------------------
  // Called by hub-profile.js when a badge is freshly unlocked. Server owns the value, is idempotent
  // per member+badge, and rejects the two server-verified badges (firstbuy/refer) — all no-ops here.
  // Inert-safe: silently does nothing if the route/backend isn't live yet.
  function award(badge) {
    if (!badge || !BASE) return Promise.resolve(null);
    return fetch(BASE + '?action=award-achievement', {
      method: 'POST', headers: authHeaders({ 'content-type': 'application/json' }), body: JSON.stringify({ achievement: badge })
    })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (s) {
        if (s && s.ok && typeof s.balance === 'number') { window.ATHLO_LOYALTY = s; paint(s); renderRedeem(s); }
        return s;
      })
      .catch(function () { return null; });
  }
  window.HubLoyalty = { award: award, reload: loadState };

  // ---- Redemption UI (points -> one-time Shopify discount code) ------------------------------
  // Rendered ONLY when the backend reports redemptionEnabled (Admin token provisioned). All money
  // logic is server-side (atomic debit, mint, refund-on-failure); the client just posts a reward id.
  function el(tag, cls, txt) { var e = document.createElement(tag); if (cls) e.className = cls; if (txt != null) e.textContent = txt; return e; }
  var redeeming = false;
  function renderRedeem(s) {
    var box = document.getElementById('lk-redeem'); if (!box) return;
    if (!s.redemptionEnabled || !s.redeemable || !s.redeemable.length) { box.hidden = true; box.textContent = ''; return; }
    box.hidden = false; box.textContent = '';
    box.appendChild(el('p', 'lk-redeem__h', 'Redeem points'));
    var opts = el('div', 'lk-redeem__opts');
    s.redeemable.forEach(function (r) {
      var b = el('button', 'lk-redeem__opt' + (r.affordable ? '' : ' is-locked'));
      b.type = 'button';
      b.appendChild(el('span', 'lk-redeem__lbl', r.label));
      b.appendChild(el('span', 'lk-redeem__cost', fmt(r.cost) + ' pts'));
      b.disabled = !r.affordable || redeeming;
      b.addEventListener('click', function () { doRedeem(r, b); });
      opts.appendChild(b);
    });
    box.appendChild(opts);
    box.appendChild(el('p', 'lk-redeem__note', 'Codes are a fixed dollar amount — use on an order of at least the reward value (any unused amount is not refunded). Single-use, locked to your account, expire in 30 days.'));
    var out = el('div', 'lk-redeem__out'); out.id = 'lk-redeem-out'; out.hidden = true; box.appendChild(out);
  }
  var ERR = { insufficient: 'Not enough points yet.', daily_cap: 'Daily redemption limit reached — try again tomorrow.',
    redemption_not_enabled: 'Redemption is not live yet.', mint_failed: 'Could not create your code — your points were refunded.',
    rate_limited: 'Too many requests — try again shortly.', unknown_reward: 'That reward is unavailable.',
    no_purchase: 'Rewards unlock with your first purchase — your points are safe and waiting.' };
  function doRedeem(r, btn) {
    if (redeeming) return;
    if (!window.confirm('Redeem ' + fmt(r.cost) + ' points for ' + r.label + '? This can’t be undone.')) return;
    redeeming = true; btn.disabled = true;
    var nonce = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : (String(Date.now()) + Math.random());
    fetch(BASE + '?action=redeem', {
      method: 'POST', headers: authHeaders({ 'content-type': 'application/json' }), body: JSON.stringify({ reward: r.id, nonce: nonce })
    })
      .then(function (resp) { return resp.json().catch(function () { return {}; }); })
      .then(function (j) {
        redeeming = false;
        if (j && j.ok && j.code) {
          // Refresh balance/affordability FIRST (it rebuilds the redeem box), THEN paint the code into
          // the fresh output — otherwise the refresh wipes the code before the member can copy it.
          loadState().then(function () {
            var out = document.getElementById('lk-redeem-out'); if (!out) return;
            out.hidden = false; out.textContent = '';
            out.appendChild(el('span', 'lk-redeem__code', j.code));
            var copy = el('button', 'lk-redeem__copy', 'Copy'); copy.type = 'button';
            copy.addEventListener('click', function () { try { navigator.clipboard.writeText(j.code); copy.textContent = 'Copied ✓'; } catch (e) {} });
            out.appendChild(copy);
            out.appendChild(el('span', 'lk-redeem__applied', r.label + ' — apply this code at checkout.'));
            out.scrollIntoView({ block: 'nearest' });
          });
        } else {
          var out = document.getElementById('lk-redeem-out');
          if (out) { out.hidden = false; out.textContent = ''; out.appendChild(el('span', 'lk-redeem__err', ERR[j && j.error] || 'Could not redeem right now.')); }
          btn.disabled = false;
        }
      })
      .catch(function () { redeeming = false; btn.disabled = false; });
  }

  // ---- Challenges: active earn-events + self-serve claim-by-code (UNIFIED POINTS) -----------------
  // Lists the active challenges (and which this member already earned) and lets a member redeem a code
  // staff hand out at events / drops / workouts. Inert-safe: if the backend route isn't live yet the
  // fetch fails and the whole card just stays hidden. The claim is server-owned + idempotent (a member
  // can't double-claim, and can't claim something staff already awarded) — the client only posts a code.
  var CHAL_ERR = {
    invalid_code: "That code isn't valid or has ended.",
    already_claimed: "You've already claimed this one.",
    code_required: 'Enter a code first.',
    rate_limited: 'Too many tries — wait a moment and retry.',
    unauthorized: 'Sign in to claim a code.'
  };
  var claiming = false, claimWired = false;

  function loadChallenges() {
    if (!BASE) return;
    fetch(BASE + '?action=challenges', { headers: authHeaders() })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (j) { if (j && j.ok) renderChallenges(j.challenges || []); })
      .catch(function () {});
  }

  function renderChallenges(list) {
    var card = document.getElementById('lk-challenges'); if (!card) return;
    if (!Array.isArray(list) || !list.length) { card.hidden = true; return; }
    card.hidden = false;
    wireClaim();
    // The claim box shows only when there's at least one code-claimable challenge left to earn.
    var anyClaimable = list.some(function (c) { return c.claimable && !c.earned; });
    var form = document.getElementById('lk-chal-claim'); if (form) form.hidden = !anyClaimable;
    var box = document.getElementById('lk-chal-list'); if (!box) return;
    box.textContent = '';
    list.forEach(function (c) {
      var row = el('div', 'lk-chal__item' + (c.earned ? ' is-earned' : ''));
      var main = el('div', 'lk-chal__main');
      main.appendChild(el('span', 'lk-chal__title', c.title));
      if (c.description) main.appendChild(el('span', 'lk-chal__desc', c.description));
      row.appendChild(main);
      row.appendChild(el('span', 'lk-chal__pts', c.earned ? 'Earned ✓' : ('+' + fmt(c.points))));
      box.appendChild(row);
    });
  }

  // Wire the claim form once (called from renderChallenges, so the DOM is guaranteed present by then).
  function wireClaim() {
    if (claimWired) return;
    var form = document.getElementById('lk-chal-claim'); if (!form) return;
    claimWired = true;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (claiming) return;
      var input = document.getElementById('lk-chal-code');
      var msg = document.getElementById('lk-chal-msg');
      var btn = document.getElementById('lk-chal-claim-btn');
      var code = ((input && input.value) || '').trim();
      if (!code) return;
      claiming = true; if (btn) btn.disabled = true;
      fetch(BASE + '?action=challenge-claim', {
        method: 'POST', headers: authHeaders({ 'content-type': 'application/json' }), body: JSON.stringify({ code: code })
      })
        .then(function (r) { return r.json().catch(function () { return {}; }); })
        .then(function (j) {
          claiming = false; if (btn) btn.disabled = false;
          if (j && j.ok) {
            if (msg) { msg.className = 'lk-chal__msg is-ok'; msg.textContent = '🎉 +' + fmt(j.points) + ' points — ' + j.title + '!'; msg.hidden = false; }
            if (input) input.value = '';
            loadState();        // balance + tier changed
            loadChallenges();   // flip this challenge to "earned" and hide the box if nothing's left
          } else if (msg) {
            msg.className = 'lk-chal__msg is-err';
            msg.textContent = CHAL_ERR[j && j.error] || 'Could not claim that code.';
            msg.hidden = false;
          }
        })
        .catch(function () { claiming = false; if (btn) btn.disabled = false; });
    });
  }

  loadChallenges();

  /* ---- Member perks (2026-10-13). Partner codes for signed-in members only - Harry's first. Asked for only
     where the Locker card exists, so no other page ever requests a code; the server answers only a member it
     has verified itself (signed App Proxy or locker token). Rendered as text nodes, never as markup. ---- */
  function loadPerks() {
    if (!BASE || !document.getElementById('lk-perks')) return;
    fetch(BASE + '?action=member-perks', { headers: authHeaders(), cache: 'no-store' })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (j) { if (j && j.ok) renderPerks(j.perks || []); })
      .catch(function () {});
  }

  function copyCode(text, btn) {
    function done(ok) {
      btn.textContent = ok ? 'Copied' : 'Select and copy';
      setTimeout(function () { btn.textContent = 'Copy code'; }, 2000);
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function () { done(true); })['catch'](function () { done(false); });
    } else done(false);
  }

  function renderPerks(list) {
    var card = document.getElementById('lk-perks'), box = document.getElementById('lk-perks-list');
    if (!card || !box) return;
    if (!Array.isArray(list) || !list.length) { card.hidden = true; return; }
    box.textContent = '';
    list.forEach(function (p) {
      if (!p || !p.code) return;
      var row = el('div', 'lk-perks__item');
      row.appendChild(el('span', 'lk-perks__name', p.name || ''));
      if (p.how_to) row.appendChild(el('p', 'lk-perks__how', p.how_to));
      var line = el('div', 'lk-perks__coderow');
      line.appendChild(el('code', 'lk-perks__code', p.code));
      var btn = el('button', 'btn btn--blue lk-perks__copy', 'Copy code');
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Copy the ' + (p.name || 'partner') + ' code');
      btn.addEventListener('click', function () { copyCode(p.code, btn); });
      line.appendChild(btn);
      row.appendChild(line);
      if (p.link_url && /^https:\/\//.test(p.link_url)) {
        var a = el('a', 'lk-perks__link', 'Visit ' + (p.name || 'partner') + ' \u2197');
        a.href = p.link_url; a.target = '_blank'; a.rel = 'noopener';
        row.appendChild(a);
      }
      box.appendChild(row);
    });
    card.hidden = !box.childNodes.length;
  }

  loadPerks();
})();
